import { MetricName } from '@canvas/frontend-metrics';

import { defineMetric, type MetricMetadataBase } from '../define-metric';
import type { FrameState } from '../stabilized-scheduler';

export const SELECTIONS_OVERLAY_CONTAINER_ID = 'selections-overlay-container';

export const HIGHLIGHT_FOCUS_BOX_CLASS_NAME = 'highlight-focus-box';

export const FOCUS_BOX_DOM_ELEMENT_ID = 'selections-focus-box';

export const TABLE_CELL_IS_SELECTED_CLASS = 'isSelected';

export type CellSelectionProbeKind = 'legacyTdIsSelected' | 'focusBox';

export function isFocusBoxVisiblyRendered(element: HTMLElement): boolean {
  if (element.style.display === 'none') {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

export function getDomSingleCellSelectedFrameState(
  tableRoot: HTMLElement,
  entryId: string,
  fieldId: string
): FrameState {
  const td = tableRoot.querySelector<HTMLElement>(
    `td[data-entry-id="${entryId}"][data-field-id="${fieldId}"]`
  );

  if (!td || !td.classList.contains(TABLE_CELL_IS_SELECTED_CLASS)) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: `${entryId}:${fieldId}` };
}

export function getFocusBoxVisibleFrameState(): FrameState {
  const byId = document.getElementById(FOCUS_BOX_DOM_ELEMENT_ID);

  if (byId && isFocusBoxVisiblyRendered(byId)) {
    return { done: true, stabilityKey: 'focus-box-visible' };
  }

  const overlay = document.getElementById(SELECTIONS_OVERLAY_CONTAINER_ID);

  if (!overlay) {
    return { done: false, stabilityKey: null };
  }

  const box = overlay.querySelector<HTMLElement>(`.${HIGHLIGHT_FOCUS_BOX_CLASS_NAME}`);

  if (!box || !isFocusBoxVisiblyRendered(box)) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: 'focus-box-visible' };
}

export interface CellSelectionProbeData {
  tableRoot: HTMLElement | null;
  selectionProbe: CellSelectionProbeKind;
  entryId: string;
  fieldId: string;
}

export interface CellSelectionMetadata extends MetricMetadataBase {
  databasesSelectionsPerformanceCohort: 'control' | 'test';
  selectionProbe: CellSelectionProbeKind;
}

export const cellSelectionMetric = defineMetric<
  CellSelectionProbeData,
  CellSelectionMetadata
>({
  metricName: MetricName.DATABASE_CELL_SELECTION_INTERACTION_TIME,
  buildProbe: (probeData) => {
    if (probeData.selectionProbe === 'legacyTdIsSelected') {
      if (!probeData.tableRoot) {
        return () => ({ done: false, stabilityKey: null });
      }

      const { tableRoot, entryId, fieldId } = probeData;

      return () => getDomSingleCellSelectedFrameState(tableRoot, entryId, fieldId);
    }

    return () => getFocusBoxVisibleFrameState();
  },
  buildMetadata: (metadata) => ({
    interactionTimingSource:
      metadata.selectionProbe === 'legacyTdIsSelected'
        ? 'dom-single-cell-isSelected-sync'
        : 'selections-focus-box-visible',
    databasesSelectionsPerformanceCohort: metadata.databasesSelectionsPerformanceCohort,
  }),
});
